import { useEffect, useRef } from "react";
import Chart from "chart.js/auto";

const CHART_COLORS = {
  text: "#5a6577",
  grid: "rgba(26, 43, 95, 0.1)",
};

const formatMonthLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-US", { month: "short" });
};

const formatCurrencyLabel = (value) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

export default function AnalyticsRevenueCharts({
  revenueSeries,
  commissionSeries,
  range,
}) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const labels = (revenueSeries || []).map((item) => item.month);
    const netProfitValues = (revenueSeries || []).map((item) => item.value);
    const commissionValues = (commissionSeries || []).map((item) => item.value);

    const cumulative = (values) => {
      let total = 0;
      return values.map((value) => {
        total += value;
        return total;
      });
    };

    const plotNetProfit =
      range === "Cumulative" ? cumulative(netProfitValues) : netProfitValues;
    const plotCommission =
      range === "Cumulative" ? cumulative(commissionValues) : commissionValues;
    const plotGrossProfit = plotNetProfit.map(
      (value, index) => value + plotCommission[index]
    );

    const dataMax = Math.max(0, ...plotGrossProfit);
    const paddedMax = dataMax * 1.15;
    const roundTo = (n, step) => Math.ceil(n / step) * step;
    const getYAxisConfig = () => {
      if (range === "Cumulative") {
        if (paddedMax <= 0) return { min: 0, max: 400000, stepSize: 50000 };
        const step = paddedMax > 500000 ? 100000 : 50000;
        const niceMax = roundTo(paddedMax, step);
        return { min: 0, max: niceMax, stepSize: step };
      }
      if (paddedMax <= 0) return { min: 0, max: 120000, stepSize: 20000 };
      const niceMax = roundTo(paddedMax, 20000);
      return { min: 0, max: Math.max(niceMax, 120000), stepSize: 20000 };
    };
    const yAxisConfig = getYAxisConfig();

    const canvas = document.getElementById("revenueChart");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (chartRef.current) {
      const chart = chartRef.current;
      chart.data.labels = labels;
      if (chart.data.datasets[0]) chart.data.datasets[0].data = plotNetProfit;
      if (chart.data.datasets[1]) chart.data.datasets[1].data = plotCommission;
      if (chart.options?.scales?.x) chart.options.scales.x.stacked = true;
      if (chart.options?.scales?.y) {
        chart.options.scales.y.stacked = true;
        chart.options.scales.y.min = yAxisConfig.min;
        chart.options.scales.y.max = yAxisConfig.max;
        chart.options.scales.y.ticks.stepSize = yAxisConfig.stepSize;
      }
      chart.update("none");
      return;
    }

    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Net Profit",
            data: plotNetProfit,
            backgroundColor: "#2d8a5f",
            borderRadius: 4,
            stack: "profit",
          },
          {
            label: "Commission",
            data: plotCommission,
            backgroundColor: "#5BA4E6",
            borderRadius: 4,
            stack: "profit",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1a2b5f",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            displayColors: true,
            itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
            callbacks: {
              title: (items) => {
                const label = items?.[0]?.label || "";
                return formatMonthLabel(label);
              },
              label: (context) => {
                const value = Number(context.parsed?.y);
                if (!Number.isFinite(value)) return "";
                return `${context.dataset.label}: ${formatCurrencyLabel(value)}`;
              },
              afterBody: (items) => {
                const grossProfit = items.reduce((sum, item) => {
                  const value = Number(item.parsed?.y);
                  return Number.isFinite(value) ? sum + value : sum;
                }, 0);
                if (!Number.isFinite(grossProfit) || grossProfit <= 0) return [];
                return [`Gross Profit: ${formatCurrencyLabel(grossProfit)}`];
              },
            },
          },
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        hover: {
          mode: "index",
          intersect: false,
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              callback: function (value) {
                const label = this.getLabelForValue(value);
                return formatMonthLabel(label);
              },
            },
          },
          y: {
            stacked: true,
            grid: { color: CHART_COLORS.grid },
            min: yAxisConfig.min,
            max: yAxisConfig.max,
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              stepSize: yAxisConfig.stepSize,
              callback: (value) => {
                const numeric = Number(value);
                if (!Number.isFinite(numeric)) return value;
                if (numeric === 0) return "$0";
                // Fractional millions so 1.0M, 1.1M, … stay distinct (Math.round made many "1M").
                if (numeric >= 1e6) {
                  return `$${(numeric / 1e6).toFixed(1)}M`;
                }
                if (numeric >= 1e3) {
                  return `$${(numeric / 1e3).toFixed(0)}K`;
                }
                return `$${numeric.toFixed(0)}`;
              },
            },
          },
        },
      },
    });
  }, [revenueSeries, commissionSeries, range]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  return (
    <div className="revenue-chart-canvas">
      <canvas id="revenueChart"></canvas>
    </div>
  );
}
